// =============================================
// CART SERVICE - API client for the new persistent cart system
// =============================================
// This service handles all cart-related API calls using the new
// backend endpoints. The cart is persistent and stored in the database,
// linked to the current user.
//
// KEY PRINCIPLES:
// 1. The cart persists across sessions - saved in DB
// 2. Selecting a table ONLY links it - does NOT clear the cart
// 3. "Send" moves items to the table's sent_items list
// =============================================

import axios from 'axios';

// Create axios instance that will be configured with auth token
const createApiClient = () => {
  const baseURL = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('token');
  
  return axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    }
  });
};

// Helper to get fresh API client with current token
const api = () => createApiClient();

// Get the current user's cart
export const getCart = async () => {
  const response = await api().get('/api/cart');
  return response.data;
};

// Add an item to the cart
export const addCartItem = async (item) => {
  const response = await api().post('/api/cart/items', {
    product_id: item.product_id,
    product_name: item.product_name,
    quantity: item.quantity || 1,
    unit_price: item.unit_price,
    destination: item.destination || 'cuisine',
    category: item.category || null,
    supplements: item.supplements || [],
    accompaniment: item.accompaniment || null,
    notes: item.notes || '',
    // Formula-specific fields
    is_formula: item.is_formula || false,
    selected_items: item.selected_items || null,
    all_items: item.all_items || null
  });
  return response.data;
};

// Update an item in the cart
export const updateCartItem = async (itemId, updates) => {
  const response = await api().put(`/api/cart/items/${itemId}`, updates);
  return response.data;
};

// Remove an item from the cart
export const removeCartItem = async (itemId) => {
  const response = await api().delete(`/api/cart/items/${itemId}`);
  return response.data;
};

// Clear the entire cart
export const clearCart = async () => {
  const response = await api().delete('/api/cart');
  return response.data;
};

// Link the cart to a table
// clearIfEmptyTable: If true and table is empty/free, clears the cart before linking
export const linkCartToTable = async (tableId, clearIfEmptyTable = false) => {
  const response = await api().post('/api/cart/link-table', {
    table_id: tableId,
    clear_if_empty_table: clearIfEmptyTable
  });
  return response.data;
};

// Unlink the cart from its table
export const unlinkCartFromTable = async () => {
  const response = await api().post('/api/cart/unlink-table');
  return response.data;
};

// Send the cart to the kitchen (moves items to table's sent_items)
export const sendToKitchen = async (options = {}) => {
  const response = await api().post('/api/cart/send', options);
  return response.data;
};

// Process a direct sale (counter/takeaway - immediate payment)
export const processDirectSale = async (paymentDetails) => {
  const response = await api().post('/api/cart/direct-sale', paymentDetails);
  return response.data;
};

// Load draft items from a table into the cart
export const loadDraftsFromTable = async (tableId) => {
  const response = await api().post(`/api/cart/load-from-table/${tableId}`);
  return response.data;
};

// Save current cart to a table as drafts
export const saveDraftsToTable = async (tableId) => {
  const response = await api().post(`/api/cart/save-to-table/${tableId}`);
  return response.data;
};

// Get detailed table info (draft_items, sent_items, totals)
export const getTableDetails = async (tableId) => {
  const response = await api().get(`/api/tables/${tableId}/details`);
  return response.data;
};

// Pay a table's bill
export const payTable = async (tableId, paymentDetails) => {
  const response = await api().post(`/api/tables/${tableId}/pay`, paymentDetails);
  return response.data;
};

// Start payment session - Sets table to "en_cours_de_paiement"
export const startPaymentSession = async (tableId) => {
  const response = await api().post(`/api/tables/${tableId}/start-payment`);
  return response.data;
};

// Process payment - Validates and finalizes the payment
export const processPayment = async (tableId, paymentData) => {
  const response = await api().post(`/api/tables/${tableId}/process-payment`, paymentData);
  return response.data;
};

// Get payment session - Gets current payment session data
export const getPaymentSession = async (tableId) => {
  const response = await api().get(`/api/tables/${tableId}/payment-session`);
  return response.data;
};

// Cancel payment session - Returns table to previous status
export const cancelPaymentSession = async (tableId) => {
  const response = await api().post(`/api/tables/${tableId}/cancel-payment`);
  return response.data;
};

// Release a table
export const releaseTable = async (tableId) => {
  const response = await api().put(`/api/tables/${tableId}/release`);
  return response.data;
};

// Cancel a table order (admin only)
export const cancelTableOrder = async (tableId, reason) => {
  const response = await api().post(`/api/tables/${tableId}/cancel`, { reason });
  return response.data;
};

// Clear a table (after payment - mark as libre and empty items)
export const clearTable = async (tableId) => {
  const response = await api().post(`/api/tables/${tableId}/clear`);
  return response.data;
};

// Export as default object for convenience
export default {
  getCart,
  addCartItem,
  updateCartItem,
  removeCartItem,
  clearCart,
  linkCartToTable,
  unlinkCartFromTable,
  sendToKitchen,
  processDirectSale,
  loadDraftsFromTable,
  saveDraftsToTable,
  getTableDetails,
  payTable,
  startPaymentSession,
  processPayment,
  getPaymentSession,
  cancelPaymentSession,
  releaseTable,
  cancelTableOrder,
  clearTable
};
